import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Button, InputBase, useMediaQuery } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import moment from 'moment';

import * as actions from '../../store/actions/index';
import ConteinerWithEjecutarPagina from '../../components/UI/ConteinerWithEjecutarPagina/ConteinerWithEjecutarPagina';
import RangoFechasSelector, { rangoFechasValido } from '../../components/UI/RangoFechasSelector/RangoFechasSelector';
import BandejaLista from '../../components/Impresion/Bandeja/BandejaLista';
import BandejaDetalle from '../../components/Impresion/Bandeja/BandejaDetalle';
import { estadosComprobante } from '../../components/Impresion/EstadoComprobanteTag/EstadoComprobanteTag';
import { claveComprobante } from '../../components/Impresion/hooks/useDatosComprobante';
import { texto } from '../../components/Impresion/textosComprobante';
import { formatNumberWithMonedaSigno } from '../../assets/shared/formatNumber';
import colores from '../../components/Impresion/Bandeja/coloresBandeja';

/**
 * Cada cuánto se refresca: el listado (estados y pedidos nuevos, sin recargarlo) y los renglones del pedido abierto,
 * que si cambiaron hacen volver a pedir el resto del detalle.
 */
const INTERVALO_REFRESCO_MS = 120000;

/** Anulado no viene marcado: lo habitual es trabajar con los pedidos vigentes. */
const ESTADOS_INICIALES = [estadosComprobante.Pendiente.nombre, estadosComprobante.Descargado.nombre];

const useStyles = makeStyles((theme) => ({
  pagina: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: 'calc(100vh - 87px)',
    minHeight: '520px',
    [theme.breakpoints.down('sm')]: {
      minHeight: '400px'
    }
  },
  // En pantallas chicas el detalle ocupa la página y se scrollea con ella.
  paginaDetalle: {
    height: 'auto'
  },
  encabezado: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '16px'
  },
  titulo: {
    fontFamily: 'Righteous, Roboto, sans-serif',
    fontSize: '28px',
    lineHeight: 1,
    color: colores.gris,
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px'
    },
    '& small': {
      display: 'block',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '13px',
      color: colores.secundario,
      marginTop: '6px'
    }
  },
  enVivo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
    fontSize: '12px',
    color: colores.secundario
  },
  punto: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: colores.ok
  },
  filtros: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '16px',
    padding: '10px 14px',
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    [theme.breakpoints.down('xs')]: {
      gap: '12px',
      padding: '10px'
    }
  },
  estados: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    paddingBottom: '2px'
  },
  botonBuscar: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  estado: {
    border: `1px solid ${colores.borde}`,
    borderRadius: '18px',
    padding: '4px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: colores.secundario,
    fontFamily: 'inherit'
  },
  buscador: {
    flex: 1,
    minWidth: '220px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: `1px solid ${colores.borde}`,
    borderRadius: '20px',
    padding: '2px 12px',
    '&:focus-within': {
      borderColor: colores.primario
    },
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      minWidth: 0
    }
  },
  split: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '400px minmax(0, 1fr)',
    gap: '12px',
    // Tablet horizontal: lista más angosta
    [theme.breakpoints.only('md')]: {
      gridTemplateColumns: '320px minmax(0, 1fr)'
    },
    // Tablet vertical y celular: se ve la lista o el detalle
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  }
}));

const filtrosIniciales = () => ({
  fechaDesde: moment().startOf('month'),
  fechaHasta: moment().startOf('day'),
  estados: ESTADOS_INICIALES
});

const filtrosConsulta = (filtros) => ({
  fechaDesde: filtros.fechaDesde.format('YYYY-MM-DD'),
  fechaHasta: filtros.fechaHasta.format('YYYY-MM-DD'),
  estados: filtros.estados
});

/** Coincide con lo que se escribe en el buscador: cliente (código o nombre), número externo o referencia. */
const coincide = (comprobante, busqueda) =>
  !busqueda ||
  [comprobante.idCliente, comprobante.nombre, comprobante.nroExterno, comprobante.nroReferencia]
    .map((valor) => texto(valor).toLowerCase())
    .some((valor) => valor.includes(busqueda));

/**
 * Pedidos: notas de pedido de ventas del Eiffel en una bandeja (lista por día a la izquierda, detalle a la derecha)
 * con su estado (Pendiente, Descargado, Anulado), el circuito de descargas y las acciones de envío. El listado se
 * refresca solo cada INTERVALO_REFRESCO_MS. En tablet vertical y celular se ve la lista o el detalle, no los dos.
 */
const Pedidos = (props) => {
  sessionStorage.setItem('seccionNombre', 'Pedidos');

  const classes = useStyles();
  // Tablet vertical y celular: la lista y el detalle no entran juntos, se pasa de uno al otro.
  const compacto = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { comprobantes, loadingComprobantes, actualizadoEl, loadingCambiarEmpresa, onLoadPedidos, onClear } = props;

  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [busqueda, setBusqueda] = useState('');
  const [seleccionado, setSeleccionado] = useState(null);
  const [mailAbierto, setMailAbierto] = useState(false);
  const [verDetalle, setVerDetalle] = useState(false);
  // Cuenta los refrescos automáticos: el detalle lo usa para revisar si cambió el pedido abierto.
  const [refresco, setRefresco] = useState(0);
  const cambiandoEmpresa = useRef(false);

  // El refresco usa los filtros de la última búsqueda, no los que se están editando.
  const filtrosAplicados = useRef(null);
  const ocupado = useRef(false);
  ocupado.current = loadingComprobantes || loadingCambiarEmpresa;

  const buscarCon = (nuevosFiltros) => {
    filtrosAplicados.current = filtrosConsulta(nuevosFiltros);
    onLoadPedidos(filtrosAplicados.current);
  };

  useEffect(() => {
    buscarCon(filtrosIniciales());

    const intervalo = setInterval(() => {
      if (document.hidden || ocupado.current || !filtrosAplicados.current) return;
      onLoadPedidos(filtrosAplicados.current, true);
      setRefresco((anterior) => anterior + 1);
    }, INTERVALO_REFRESCO_MS);

    return () => {
      clearInterval(intervalo);
      onClear();
    };
  }, []);

  // Al terminar el cambio de empresa se vuelve a buscar con los filtros iniciales.
  useEffect(() => {
    if (loadingCambiarEmpresa) {
      cambiandoEmpresa.current = true;
    } else if (cambiandoEmpresa.current) {
      cambiandoEmpresa.current = false;
      onClear();
      const iniciales = filtrosIniciales();
      setFiltros(iniciales);
      buscarCon(iniciales);
    }
  }, [loadingCambiarEmpresa]);

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return comprobantes.filter((comprobante) => coincide(comprobante, texto));
  }, [comprobantes, busqueda]);

  // Se mantiene el elegido entre refrescos; si ya no está en la lista se elige el primero.
  const elegido = visibles.find((comprobante) => claveComprobante(comprobante) === seleccionado) || visibles[0] || null;
  const claveElegido = claveComprobante(elegido);

  // El mail es del comprobante elegido: al cambiar de comprobante se cierra.
  useEffect(() => setMailAbierto(false), [claveElegido]);

  // Con el detalle a pantalla completa, si el pedido abierto dejó de estar en la lista (refresco, filtro) se vuelve a
  // la lista en lugar de mostrar otro sin avisar.
  const mostrandoDetalle = compacto && verDetalle;
  const abiertoEnLista = visibles.some((comprobante) => claveComprobante(comprobante) === seleccionado);
  useEffect(() => {
    if (mostrandoDetalle && !abiertoEnLista && !loadingComprobantes) setVerDetalle(false);
  }, [mostrandoDetalle, abiertoEnLista, loadingComprobantes]);

  useEffect(() => {
    if (mostrandoDetalle) window.scrollTo(0, 0);
  }, [mostrandoDetalle]);

  const seleccionar = (comprobante) => {
    setSeleccionado(claveComprobante(comprobante));
    if (compacto) setVerDetalle(true);
  };

  const abrirMailDesdeLista = () => {
    if (!elegido || elegido.estado === estadosComprobante.Anulado.nombre) return;
    setSeleccionado(claveElegido);
    setMailAbierto(true);
    if (compacto) setVerDetalle(true);
  };

  const cambiarEstado = (estado) =>
    setFiltros({
      ...filtros,
      estados: filtros.estados.includes(estado) ? filtros.estados.filter((e) => e !== estado) : [...filtros.estados, estado]
    });

  const filtrosValidos = rangoFechasValido(filtros) && filtros.estados.length > 0;
  const estadosBuscados = filtrosAplicados.current ? filtrosAplicados.current.estados : ESTADOS_INICIALES;
  const total = visibles.reduce((suma, comprobante) => suma + (comprobante.estado === estadosComprobante.Anulado.nombre ? 0 : comprobante.total), 0);

  return (
    <ConteinerWithEjecutarPagina showMultiempresa isExecuted={false}>
      <div className={`${classes.pagina} ${mostrandoDetalle ? classes.paginaDetalle : ''}`}>
        <div className={classes.encabezado}>
          <div className={classes.titulo}>
            Pedidos
            <small>{`${visibles.length} pedidos · ${formatNumberWithMonedaSigno(total)}`}</small>
          </div>
          <div className={classes.enVivo}>
            <span className={classes.punto} />
            {actualizadoEl ? `Actualizado a las ${moment(actualizadoEl).format('HH:mm')}` : 'Actualizando…'}
          </div>
        </div>

        <div className={classes.filtros}>
          <RangoFechasSelector
            fechaDesde={filtros.fechaDesde}
            fechaHasta={filtros.fechaHasta}
            onChange={(rango) => setFiltros({ ...filtros, ...rango })}
          />
          <div className={classes.estados}>
            {Object.values(estadosComprobante).map((estado) => {
              const marcado = filtros.estados.includes(estado.nombre);
              const cantidad = comprobantes.filter((comprobante) => comprobante.estado === estado.nombre).length;
              return (
                <button
                  type="button"
                  key={estado.nombre}
                  className={classes.estado}
                  style={marcado ? { backgroundColor: estado.color, borderColor: estado.color, color: estado.textColor } : undefined}
                  onClick={() => cambiarEstado(estado.nombre)}
                >
                  {estado.nombre}
                  {estadosBuscados.includes(estado.nombre) && ` · ${cantidad}`}
                </button>
              );
            })}
          </div>
          <Button
            className={classes.botonBuscar}
            color="primary"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={!filtrosValidos || loadingComprobantes}
            onClick={() => buscarCon(filtros)}
          >
            Buscar
          </Button>
          <div className={classes.buscador}>
            <SearchIcon style={{ color: colores.tenue }} fontSize="small" />
            <InputBase
              fullWidth
              placeholder="Filtrar por cliente, número externo o referencia…"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
            />
          </div>
        </div>

        <div className={classes.split}>
          {!mostrandoDetalle && (
            <BandejaLista
              comprobantes={visibles}
              loading={loadingComprobantes}
              seleccionado={claveElegido}
              onSeleccionar={seleccionar}
              onMail={abrirMailDesdeLista}
            />
          )}
          {(!compacto || mostrandoDetalle) && (
            <BandejaDetalle
              comprobante={elegido}
              refresco={refresco}
              mailAbierto={mailAbierto}
              onAbrirMail={() => setMailAbierto(true)}
              onCerrarMail={() => setMailAbierto(false)}
              onVolver={compacto ? () => setVerDetalle(false) : undefined}
            />
          )}
        </div>
      </div>
    </ConteinerWithEjecutarPagina>
  );
};

const mapStateToProps = (state) => {
  return {
    comprobantes: state.impresion.comprobantes.comprobantes,
    loadingComprobantes: state.impresion.comprobantes.loading,
    actualizadoEl: state.impresion.comprobantes.actualizadoEl,
    loadingCambiarEmpresa: state.multiempresa.loadingCambiarEmpresa
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onLoadPedidos: (filtros, silencioso) => dispatch(actions.loadPedidosImpresion(filtros, silencioso)),
    onClear: () => dispatch(actions.clearImpresion())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Pedidos);
